import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Tooltip, Empty } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MessageOutlined,
  CalculatorOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import { agentAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Left sidebar of the chat landing page: new chat, chat history
 * (renamable, persisted server-side), saved cost estimations and, for
 * administrators, the project upload (Datenimport).
 */
const ChatSidebar = ({
  chats,
  activeConversationId,
  onNewChat,
  onSelectChat,
  onChatsChanged,
  estimations,
  isStreaming,
  onUpload,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canUpload = hasPermission('datenimport') && typeof onUpload === 'function';
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  const startRename = (chat) => {
    setEditingId(chat.id);
    setEditingTitle(chat.title || '');
  };

  const commitRename = async () => {
    const title = editingTitle.trim();
    const id = editingId;
    setEditingId(null);
    if (!id || !title) return;
    try {
      await agentAPI.renameConversation(id, title);
      onChatsChanged();
    } catch (e) {
      console.error('Rename failed', e);
    }
  };

  const handleDelete = async (chatId) => {
    try {
      await agentAPI.deleteConversation(chatId);
      if (chatId === activeConversationId) onNewChat();
      onChatsChanged();
    } catch (e) {
      console.error('Delete failed', e);
    }
  };

  const handleContinueEstimation = (estimation) => {
    navigate('/estimate', {
      state: {
        estimationId: estimation.id,
        estimationName: estimation.name,
        from: '/',
      },
    });
  };

  return (
    <div className="agent-sider-content">
      <Button
        type="primary"
        block
        icon={<PlusOutlined />}
        onClick={onNewChat}
        disabled={isStreaming}
      >
        Neuer Chat
      </Button>

      <div className="agent-sider-section">
        <div className="agent-sider-heading"><MessageOutlined /> Chats</div>
        {chats.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Noch keine Chats" />
        ) : (
          <div className="agent-sider-list">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`agent-chat-item ${chat.id === activeConversationId ? 'agent-chat-item-active' : ''}`}
              >
                {editingId === chat.id ? (
                  <Input
                    size="small"
                    value={editingTitle}
                    autoFocus
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onPressEnter={commitRename}
                    onBlur={commitRename}
                  />
                ) : (
                  <>
                    <span
                      className="agent-chat-item-title"
                      title={chat.title}
                      onClick={() => !isStreaming && onSelectChat(chat.id)}
                    >
                      {chat.title || 'Unbenannter Chat'}
                    </span>
                    <span className="agent-chat-item-actions">
                      <Tooltip title="Umbenennen">
                        <EditOutlined onClick={() => startRename(chat)} />
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <DeleteOutlined onClick={() => handleDelete(chat.id)} />
                      </Tooltip>
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {hasPermission('kostenschaetzung') && (
      <div className="agent-sider-section">
        <div className="agent-sider-heading"><CalculatorOutlined /> Gespeicherte Kostenschätzungen</div>
        {estimations.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Keine gespeicherten Kostenschätzungen" />
        ) : (
          <div className="agent-sider-list">
            {estimations.map((estimation) => (
              <div
                key={estimation.id}
                className="agent-chat-item"
                onClick={() => handleContinueEstimation(estimation)}
              >
                <span className="agent-chat-item-title" title={estimation.name}>
                  {estimation.name}
                </span>
                <span className="agent-estimation-date">
                  {estimation.updated_at
                    ? new Date(estimation.updated_at).toLocaleDateString('de-CH')
                    : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {canUpload && (
        <div className="agent-sider-section agent-sider-admin">
          <div className="agent-sider-heading"><CloudUploadOutlined /> Datenimport</div>
          <Button block icon={<CloudUploadOutlined />} onClick={onUpload}>
            Projekte hochladen
          </Button>
          <div className="agent-sider-note">
            Projektordner im Ablage-Schema hinzufügen; vorhandene Projekte werden übersprungen.
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSidebar;
